import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { hasPermission } from "../entities/access";
import type { JsonValue } from "../api";
import type { ModuleDefinition } from "../entities/modules";
import { shellBootstrapKey, shellBootstrapQuery } from "./bootstrap";
import { EmptyState } from "./states";
import type { ShellServices } from "./services";
import { Notice } from "./Notice";

function ModuleSettings({
  module,
  services,
  allowed,
}: {
  module: ModuleDefinition;
  services: ShellServices;
  allowed: boolean;
}) {
  const settings = useQuery({
    queryKey: ["modules", module.key, "settings"],
    queryFn: () => services.modules.settings!(module.key),
    enabled: allowed,
  });
  const [value, setValue] = useState<string | null>(null);
  const [parseError, setParseError] = useState(false);
  const save = useMutation({
    mutationFn: (settings: Record<string, JsonValue>) =>
      services.modules.updateSettings!(module.key, settings),
    onSuccess: () => {
      void settings.refetch();
      setValue(null);
    },
  });
  if (!allowed) return null;
  const source =
    value ??
    (settings.data
      ? JSON.stringify(settings.data.data.settings, null, 2)
      : "{}");
  return (
    <details className="module-settings">
      <summary>Настройки</summary>
      <label>
        <span className="visually-hidden">
          Настройки модуля {module.display_name}
        </span>
        <textarea
          value={source}
          onChange={(event) => {
            setValue(event.target.value);
            setParseError(false);
          }}
          rows={7}
          spellCheck={false}
        />
      </label>
      <button
        className="button button--secondary button--compact"
        type="button"
        disabled={save.isPending}
        onClick={() => {
          try {
            const parsed: unknown = JSON.parse(source);
            if (
              parsed === null ||
              Array.isArray(parsed) ||
              typeof parsed !== "object"
            )
              throw new Error();
            setParseError(false);
            save.mutate(parsed as Record<string, JsonValue>);
          } catch {
            setParseError(true);
          }
        }}
      >
        Сохранить настройки
      </button>
      {save.isError || parseError ? (
        <Notice>
          Настройки должны быть корректным JSON-объектом и соответствовать схеме
          модуля.
        </Notice>
      ) : null}
    </details>
  );
}

function ModuleCard({
  module,
  services,
  canConfigure,
  allOrganizations,
}: {
  module: ModuleDefinition;
  services: ShellServices;
  canConfigure: boolean;
  allOrganizations: boolean;
}) {
  const client = useQueryClient();
  const toggle = useMutation({
    mutationFn: () =>
      module.enabled
        ? services.modules.disable!(module.key)
        : services.modules.enable!(module.key),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["modules"] }),
        client.invalidateQueries({ queryKey: shellBootstrapKey }),
      ]);
    },
  });
  const action = module.enabled ? "отключить" : "включить";
  const actionLabel = allOrganizations
    ? `${module.enabled ? "Отключить" : "Включить"} везде`
    : module.enabled
      ? "Отключить"
      : "Включить";
  const actionScope = allOrganizations ? " во всех доступных организациях" : "";
  return (
    <article className="module-card">
      <header>
        <div>
          <h2>{module.display_name}</h2>
          <p>
            {module.key} · версия {module.version}
          </p>
        </div>
        <span
          className={`status-badge status-badge--${module.enabled ? "ready" : "expired"}`}
        >
          {module.enabled ? "Включён" : "Отключён"}
        </span>
      </header>
      <dl>
        <div>
          <dt>Состояние</dt>
          <dd>
            {module.state} · {module.health_state}
          </dd>
        </div>
        <div>
          <dt>Права</dt>
          <dd>
            {module.permissions.length
              ? module.permissions.map((item) => item.key).join(", ")
              : "Нет"}
          </dd>
        </div>
        <div>
          <dt>Меню</dt>
          <dd>
            {module.menu.length
              ? module.menu.map((item) => item.label).join(", ")
              : "Нет"}
          </dd>
        </div>
      </dl>
      {canConfigure ? (
        <div className="module-actions">
          <button
            className={
              module.enabled
                ? "button button--danger"
                : "button button--primary"
            }
            type="button"
            disabled={toggle.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Подтвердите: ${action} модуль «${module.display_name}»${actionScope}. Данные модуля не будут удалены.`,
                )
              )
                toggle.mutate();
            }}
          >
            {toggle.isPending ? "Применяем…" : actionLabel}
          </button>
          {toggle.isError ? (
            <p className="module-action-error" role="alert">
              Не удалось изменить доступность. Прежнее состояние сохранено.
              Повторите попытку.
            </p>
          ) : null}
        </div>
      ) : null}
      <ModuleSettings
        module={module}
        services={services}
        allowed={canConfigure && !allOrganizations}
      />
    </article>
  );
}

export function ModulesAdminPage({ services }: { services: ShellServices }) {
  const bootstrap = useQuery(shellBootstrapQuery(services));
  const modules = useQuery({
    queryKey: ["modules"],
    queryFn: () => services.modules.list!(),
    retry: false,
  });
  const canConfigure = hasPermission(
    { permissions: bootstrap.data?.permissions ?? [] },
    "modules.configure",
  );
  const allOrganizations = bootstrap.data?.allOrganizations ?? false;
  return (
    <main className="content-page" aria-labelledby="modules-title">
      <header className="page-header">
        <div>
          <p className="eyebrow">Конфигурация ERP</p>
          <h1 id="modules-title">Модули</h1>
          <p>
            Реестр показывает версии, состояния, права и пункты меню. Отключение
            влияет на доступность и навигацию, но не удаляет данные.
          </p>
          {allOrganizations ? (
            <p className="module-scope-note">
              Изменения применяются сразу ко всем доступным организациям. Для
              настройки отдельной организации выберите её в верхней панели.
            </p>
          ) : null}
        </div>
      </header>
      {modules.isLoading ? (
        <p aria-busy="true">Загружаем реестр модулей…</p>
      ) : modules.isError ? (
        <Notice>Не удалось загрузить реестр модулей.</Notice>
      ) : modules.data?.data.length ? (
        <div className="module-grid">
          {modules.data.data.map((module) => (
            <ModuleCard
              key={module.id}
              module={module}
              services={services}
              canConfigure={canConfigure}
              allOrganizations={allOrganizations}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          title="Подключённых модулей нет"
          description="Реестр не вернул доступных модулей для выбранной организации."
        />
      )}
    </main>
  );
}
